import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useThemeStore = defineStore('theme', () => {
  const isDarkMode = ref(false)
  const isSystemAdmin = ref(false)

  // Initialize theme from localStorage or default to light mode
  const initializeTheme = () => {
    const savedTheme = localStorage.getItem('teamio_theme')
    const savedAdminStatus = localStorage.getItem('teamio_is_admin')
    
    if (savedTheme === 'dark' && savedAdminStatus === 'true') {
      isDarkMode.value = true
    }
    
    isSystemAdmin.value = savedAdminStatus === 'true'
    
    // Apply theme to document
    applyTheme()
  }

  // Toggle between light and dark mode (admin only)
  const toggleTheme = () => {
    if (!isSystemAdmin.value) {
      console.warn('Theme toggle is only available for system administrators')
      return
    }
    
    isDarkMode.value = !isDarkMode.value
    localStorage.setItem('teamio_theme', isDarkMode.value ? 'dark' : 'light')
    applyTheme()
  }

  // Set admin status (called when user logs in/out)
  const setAdminStatus = (isAdmin: boolean) => {
    isSystemAdmin.value = isAdmin
    localStorage.setItem('teamio_is_admin', isAdmin.toString())
    
    // If user is no longer admin, reset to light mode
    if (!isAdmin && isDarkMode.value) {
      isDarkMode.value = false
      localStorage.setItem('teamio_theme', 'light')
      applyTheme()
    }
  }

  // Apply theme to document
  const applyTheme = () => {
    const html = document.documentElement
    
    if (isDarkMode.value && isSystemAdmin.value) {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
    }
  }

  // Computed properties
  const canToggleTheme = computed(() => isSystemAdmin.value)
  const currentTheme = computed(() => isDarkMode.value ? 'dark' : 'light')

  return {
    isDarkMode,
    isSystemAdmin,
    canToggleTheme,
    currentTheme,
    initializeTheme,
    toggleTheme,
    setAdminStatus,
    applyTheme
  }
})
